import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from "@tanstack/react-table";
import { getSortDirection } from "../../pages/Campaigns/components/CampaignTable/utils/functions";
import { TableProps } from "./utils/types";
import TableShimmer from "./TableShimmer";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import { useScrollRestoration } from "../../hooks/useScrollRestoration";

const Table = <TData,>({
  COLUMNS,
  COLUMNS_DATA,
  tableHeader = true,
  children,
  extraClass = "",
  cellClassName = "",
  maxHeight,
  minHeight,
  pinnedColumns = [],
  onRowHover,
  onCursorPosition,
  floatingActions,
  isFloatingActionsHovered = false,
  isPopupOpen = false,
  onCursorOverHeader,
  ariaLabel = "Data table",
  isLoading = false,
  rowCount = 10,
  onRowClick,
  emptyContent,
  getRowId,
  selectedRowId,
  selectedRowClassName = "",
  scrollKey,
}: TableProps<TData>) => {
  const setScrollRef = useScrollRestoration(scrollKey);
  const [focusedCell, setFocusedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  const floatingHoveredRef = useRef(isFloatingActionsHovered);
  const popupOpenRef = useRef(isPopupOpen);
  useEffect(() => {
    floatingHoveredRef.current = isFloatingActionsHovered;
    popupOpenRef.current = isPopupOpen;
  }, [isFloatingActionsHovered, isPopupOpen]);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const table = useReactTable({
    data: COLUMNS_DATA ?? [],
    columns: COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });

  const pinnedColumnIds = pinnedColumns;

  const getColStyle = useCallback(
    (columnDef: ColumnDef<TData, unknown>): React.CSSProperties | undefined => {
      const size = columnDef.size;
      const minSize = columnDef.minSize;
      const maxSize = columnDef.maxSize;
      const hasNoExplicitSize = !size || size === 150;
      if (minSize && hasNoExplicitSize) {
        return { minWidth: `${minSize}px` };
      }
      if (size && size !== 150) {
        return {
          width: `${size}px`,
          ...(minSize && { minWidth: `${minSize}px` }),
          ...(maxSize && { maxWidth: `${maxSize}px` }),
        };
      }
      return undefined;
    },
    []
  );

  const { pinnedColGroup, unpinnedColGroup } = useMemo(() => {
    const headers = table.getHeaderGroups()?.[0]?.headers ?? [];
    const pinnedHeaders = headers.filter((header) => {
      const columnId =
        (header.column.columnDef as { accessorKey?: string })?.accessorKey ??
        header.column.id ??
        "";
      return pinnedColumnIds.includes(columnId);
    });
    const unpinnedHeaders = headers.filter((header) => {
      const columnId =
        (header.column.columnDef as { accessorKey?: string })?.accessorKey ??
        header.column.id ??
        "";
      return !pinnedColumnIds.includes(columnId);
    });
    return {
      pinnedColGroup:
        pinnedHeaders.length > 0 ? (
          <colgroup>
            {pinnedHeaders.map((header, idx) => (
              <col key={idx} style={getColStyle(header.column.columnDef)} />
            ))}
          </colgroup>
        ) : null,
      unpinnedColGroup: (
        <colgroup>
          {unpinnedHeaders.map((header, idx) => (
            <col key={idx} style={getColStyle(header.column.columnDef)} />
          ))}
        </colgroup>
      ),
    };
  }, [table, pinnedColumnIds, getColStyle]);

  const handleRowHover = useCallback(
    (
      rowIndex: number | null,
      rowRef?: React.RefObject<HTMLTableRowElement>
    ) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (rowIndex !== null) {
        onRowHover?.(rowIndex, rowRef);
        return;
      }

      if (floatingHoveredRef.current || popupOpenRef.current) {
        return;
      }

      if (floatingActions) {
        hoverTimeoutRef.current = setTimeout(() => {
          if (!floatingHoveredRef.current && !popupOpenRef.current) {
            onRowHover?.(null);
          }
          hoverTimeoutRef.current = null;
        }, 80);
      } else {
        onRowHover?.(null);
      }
    },
    [onRowHover, floatingActions]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (floatingHoveredRef.current || popupOpenRef.current) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const isOverHeader = tableHeader && y < 60;
      onCursorOverHeader?.(isOverHeader);

      onCursorPosition?.({ x, y });
    },
    [onCursorPosition, tableHeader, onCursorOverHeader]
  );

  const handleMouseLeave = useCallback(() => {
    if (floatingHoveredRef.current || popupOpenRef.current) {
      return;
    }

    if (floatingActions) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        if (!floatingHoveredRef.current && !popupOpenRef.current) {
          onRowHover?.(null);
          onCursorPosition?.(null);
          onCursorOverHeader?.(false);
        }
        hoverTimeoutRef.current = null;
      }, 80);
    } else {
      onRowHover?.(null);
      onCursorPosition?.(null);
      onCursorOverHeader?.(false);
    }
  }, [onRowHover, onCursorPosition, onCursorOverHeader, floatingActions]);

  const handleKeyDown = (
    event: React.KeyboardEvent,
    rowIndex: number,
    colIndex: number
  ) => {
    const totalRows = table.getRowModel().rows?.length ?? 0;
    const totalCols = table.getAllLeafColumns()?.length ?? 0;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        if (colIndex < totalCols - 1) {
          setFocusedCell({ row: rowIndex, col: colIndex + 1 });
        }
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (colIndex > 0) {
          setFocusedCell({ row: rowIndex, col: colIndex - 1 });
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (rowIndex < totalRows - 1) {
          setFocusedCell({ row: rowIndex + 1, col: colIndex });
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (rowIndex > 0) {
          setFocusedCell({ row: rowIndex - 1, col: colIndex });
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();

        handleRowHover(rowIndex, {
          current: event.currentTarget.closest("tr") as HTMLTableRowElement,
        });
        break;
      case "Escape":
        event.preventDefault();
        handleRowHover(null);
        setFocusedCell(null);
        break;
    }
  };

  const isEmpty = !COLUMNS_DATA || COLUMNS_DATA.length === 0;

  const heightStyle = maxHeight
    ? {
        maxHeight: `calc(${maxHeight} - 2px)`,
        minHeight: `calc(${maxHeight} - 2px)`,
      }
    : minHeight && !isEmpty
      ? { minHeight: `calc(${minHeight} - 2px)` }
      : {};

  const renderPinnedColumns = () => {
    if (pinnedColumnIds?.length === 0) return null;

    return (
      <div
        data-table-columns
        className={`sticky left-0 z-20 border-r-2 border-border-gray dark:border-border-gray-dk overflow-y-auto ${visibleScrollbar(
          "[&::-webkit-scrollbar]:h-[6px]"
        )}`}
        style={heightStyle}
      >
        <table
          className="border-collapse min-w-full"
          style={{ tableLayout: "fixed" }}
          role="grid"
          aria-label={`${ariaLabel} - Pinned columns`}
        >
          {pinnedColGroup}
          {tableHeader && (
            <thead className="sticky top-0 z-10" role="rowgroup">
              {table.getHeaderGroups()?.map((headerGroup, index) => (
                <tr key={headerGroup.id ?? index} role="row">
                  {headerGroup.headers
                    .filter((header) => {
                      const columnId =
                        (header.column.columnDef as { accessorKey?: string })
                          ?.accessorKey ??
                        header.column.id ??
                        "";
                      return pinnedColumnIds.includes(columnId);
                    })
                    .map((header, index) => (
                      <th
                        key={header.id ?? index}
                        className={`text-md font-medium text-gray-375 dark:text-gray-375-dk bg-table-header-pinned dark:bg-table-header-pinned-dk border-b border-gray-75 dark:border-gray-75-dk whitespace-nowrap hover:bg-gray-75 dark:hover:bg-gray-75-dk transition-colors duration-200 ${
                          index === 0 ? "rounded-tl-[6px]" : ""
                        }`}
                        role="columnheader"
                        aria-sort={getSortDirection(
                          header.column.getIsSorted()
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody role="rowgroup">
            {table.getRowModel()?.rows?.map((row, rowIndex) => {
              const isSelected =
                getRowId && selectedRowId
                  ? getRowId(row.original as TData) === selectedRowId
                  : false;
              return (
                <tr
                  key={row.id ?? rowIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onRowClick) {
                      const target = e.target as Element;
                      const isInteractive = target.closest(
                        "[data-popup-content], [data-three-dots-button], button, a, [role='button']"
                      );
                      if (isInteractive) return;
                      onRowClick?.(row.original as TData);
                    }
                  }}
                  className={`${
                    table.getRowModel()?.rows?.length < 10
                      ? "border-b border-gray-75 dark:border-gray-75-dk"
                      : rowIndex === table.getRowModel()?.rows?.length - 1
                        ? ""
                        : "border-b border-gray-75 dark:border-gray-75-dk"
                  } group/row transition-colors duration-200 ${
                    isSelected
                      ? selectedRowClassName
                      : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  } ${onRowClick ? "cursor-pointer" : ""}`}
                  onMouseEnter={(e) =>
                    handleRowHover(rowIndex, { current: e.currentTarget })
                  }
                  onMouseLeave={() => handleRowHover(null)}
                  role="row"
                  aria-rowindex={rowIndex + 1}
                >
                  {row
                    .getVisibleCells()
                    .filter((cell) => {
                      const columnId =
                        (cell.column.columnDef as { accessorKey?: string })
                          ?.accessorKey ??
                        cell.column.id ??
                        "";
                      return pinnedColumnIds.includes(columnId);
                    })
                    .map((cell, colIndex) => (
                      <td
                        key={cell.id ?? colIndex}
                        className={`px-[12px] h-[60px] whitespace-nowrap ${cellClassName}`}
                        role="gridcell"
                        tabIndex={
                          focusedCell?.row === rowIndex &&
                          focusedCell?.col === colIndex
                            ? 0
                            : -1
                        }
                        onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        aria-selected={
                          focusedCell?.row === rowIndex &&
                          focusedCell?.col === colIndex
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderUnpinnedColumns = () => {
    return (
      <div
        data-table-columns
        className={`flex-1 overflow-auto ${visibleScrollbar(
          "[&::-webkit-scrollbar]:h-[6px] [&::-webkit-scrollbar]:w-[6px]"
        )}`}
        style={heightStyle}
      >
        <table
          className="border-collapse min-w-full"
          role="grid"
          aria-label={`${ariaLabel} - Unpinned columns`}
        >
          {unpinnedColGroup}
          {tableHeader && (
            <thead className="sticky top-0 z-10" role="rowgroup">
              {table.getHeaderGroups()?.map((headerGroup, index) => (
                <tr key={headerGroup.id ?? index} role="row">
                  {headerGroup.headers
                    .filter((header) => {
                      const columnId =
                        (header.column.columnDef as { accessorKey?: string })
                          ?.accessorKey ??
                        header.column.id ??
                        "";
                      return !pinnedColumnIds.includes(columnId);
                    })
                    .map((header, index) => {
                      const isLastColumn =
                        index ===
                        (headerGroup.headers?.filter((h) => {
                          const columnId =
                            (h.column.columnDef as { accessorKey?: string })
                              ?.accessorKey ??
                            h.column.id ??
                            "";
                          return !pinnedColumnIds?.includes(columnId);
                        })?.length ?? 0) -
                          1;

                      return (
                        <th
                          key={header.id ?? index}
                          className={`text-md font-medium text-gray-375 dark:text-gray-375-dk bg-table-header dark:bg-table-header-dk border-b border-gray-75 dark:border-gray-75-dk whitespace-nowrap hover:bg-gray-75 dark:hover:bg-gray-75-dk transition-colors duration-200 ${
                            pinnedColumnIds?.length === 0 && index === 0
                              ? "rounded-tl-[6px]"
                              : ""
                          } ${isLastColumn ? "rounded-tr-[6px]" : ""}`}
                          style={getColStyle(header.column.columnDef)}
                          role="columnheader"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      );
                    })}
                </tr>
              ))}
            </thead>
          )}
          <tbody role="rowgroup">
            {table.getRowModel()?.rows?.map((row, index) => {
              const isSelected =
                getRowId && selectedRowId
                  ? getRowId(row.original as TData) === selectedRowId
                  : false;
              return (
                <tr
                  key={row.id ?? index}
                  className={`${
                    table.getRowModel()?.rows?.length < 10
                      ? "border-b border-gray-75 dark:border-gray-75-dk"
                      : index === table.getRowModel()?.rows?.length - 1
                        ? ""
                        : "border-b border-gray-75 dark:border-gray-75-dk"
                  } group/row transition-colors duration-200 ${
                    isSelected
                      ? selectedRowClassName
                      : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  } ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onRowClick) {
                      const target = e.target as Element;
                      const isInteractive = target.closest(
                        "[data-popup-content], [data-three-dots-button], button, a, [role='button']"
                      );
                      if (isInteractive) return;
                      onRowClick?.(row.original as TData);
                    }
                  }}
                  onMouseEnter={(e) =>
                    handleRowHover(index, { current: e.currentTarget })
                  }
                  onMouseLeave={() => handleRowHover(null)}
                  role="row"
                  aria-rowindex={index + 1}
                >
                  {row
                    .getVisibleCells()
                    .filter((cell) => {
                      const columnId =
                        (cell.column.columnDef as { accessorKey?: string })
                          ?.accessorKey ??
                        cell.column.id ??
                        "";
                      return !pinnedColumnIds.includes(columnId);
                    })
                    .map((cell, index) => (
                      <td
                        key={cell.id ?? index}
                        className={`px-[12px] h-[60px] whitespace-nowrap ${cellClassName}`}
                        style={getColStyle(cell.column.columnDef)}
                        role="gridcell"
                        tabIndex={
                          focusedCell?.row === index &&
                          focusedCell?.col === index
                            ? 0
                            : -1
                        }
                        onKeyDown={(e) => handleKeyDown(e, index, index)}
                        aria-selected={
                          focusedCell?.row === index &&
                          focusedCell?.col === index
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {table.getRowModel()?.rows?.length === 0 && !isLoading && (
          <>
            {emptyContent ? (
              emptyContent
            ) : (
              <div className="flex items-center justify-center py-10">
                <span className="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                  No data found
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <TableShimmer rowCount={rowCount} />;
  }

  return (
    <div
      ref={setScrollRef}
      className={`w-full border border-border-gray dark:border-border-gray-dk rounded-[8px] ${extraClass}`}
      style={heightStyle}
      role="region"
      aria-label={ariaLabel}
    >
      {children}
      <div
        className="flex rounded-[8px]"
        data-testid="table-container"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {renderPinnedColumns()}
        {renderUnpinnedColumns()}
        {floatingActions && (
          <div role="complementary" aria-label="Table actions">
            {floatingActions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;
